import { AlertCircle, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { tx } from '../i18n';
import { guidanceFor } from '../lib/errors';

interface ErrorPanelProps {
  /** Whatever was thrown: an ApiError gets specific guidance, anything else gets the general kind. */
  cause: unknown;
  /** The heading to fall back to when the code is one we have no specific guidance for. */
  title?: string;
  onRetry?: () => void;
  children?: ReactNode;
}

/**
 * A failure explained: what happened, what it means, and what to do about it.
 *
 * <p>The retry button appears only when retrying could work. Offering it after a mistyped postcode
 * teaches the reader to press it at random; withholding it says, without a sentence, that the fix
 * is in their hands and points them at it.
 */
export default function ErrorPanel({ cause, title, onRetry, children }: ErrorPanelProps) {
  const guidance = guidanceFor(cause, title);

  return (
    <div className="notice notice--error" role="alert">
      <AlertCircle size={20} aria-hidden="true" />
      <div className="notice__body">
        <p className="notice__title">{guidance.title}</p>
        <p className="small">{guidance.detail}</p>
        {children}
      </div>
      {onRetry && guidance.retry ? (
        <button type="button" className="button button--secondary button--small" onClick={onRetry}>
          <RefreshCw size={15} aria-hidden="true" />
          {tx('Try again', 'Tekrar dene', 'Spróbuj ponownie', 'Tentar novamente', 'Reintentar')}
        </button>
      ) : null}
    </div>
  );
}
